import React, {useState, useCallback, useEffect, useMemo} from "react";
import {Link, NavLink, Prompt, Redirect, Route, Switch, useHistory, useParams, useRouteMatch} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import { BookOpen, Files, Settings, Loader2 } from "lucide-react";
import { ReactComponent as BackArrow } from "../../svg/back.svg";
import { ReactComponent as TestIcon } from "../../svg/test.svg";
import {ReactComponent as StatusIcon} from "../../svg/status.svg";
import {Modal, PopupProvider, Skeleton, StatusText, usePopup} from "../../UIElements";
import BotVersionSelector from "./components/BotVersionSelector";
import "./SupportBotEditor.scss"
import BotInformation from "./BotInformation";
import {useSupportBotData} from "./SupportBotData";
import BotInstructions from "./BotInstructions";
import BotMaterials from "./BotMaterials";
import {useSnackbar} from "../../Content";
import {PostCodes} from "../../api/APISurvey";
import {
    AddFileToVectorStore,
    CreateVectorStore,
    GetAssistant, GetVectorStoreFiles, PostAssistant, PostAssistantWithId, PostBot, PostBotWithId, RemoveFileFromVectorStore
} from "../../api/APIBot";
import {getUniqueKey} from "../../Formats";
import BotModuleTester from "./components/BotModuleTester";
import {useLanguageData} from "../../App";
import {processSingleBotInstructions, replaceInstructionVariables} from "../../utils/assistantUtils";
import {syncVectorStoreFiles, ensureFileSearchTool} from "../../utils/vectorStoreUtils";
import BotSessionData from "../../utils/botSessionStorage";
import _ from "lodash";

export const SupportBotEditor = () => {
    const { botId } = useParams();
    const intl = useIntl();
    let { url, path } = useRouteMatch();
    const [initialDataLoaded, setInitialDataLoaded] = useState(false)

    const {
        isLoading,
        isError,
        isSuccess,
        refreshData,
        data: {
            supportRoles,
            supportBots,
            supportTopicCodes
        } = {}
    } = useSupportBotData();

    useEffect(() => {
        if (isSuccess && supportBots && !initialDataLoaded) {
            setInitialDataLoaded(true)
        }
    }, [isSuccess, supportBots, initialDataLoaded])

    if (isLoading) {
        return <BotEditorSkeleton />
    }

    if (isError) {
        return <div className={"survey-container bot-editor"}>
            <div className={"survey-content"}>
                <h3>
                    {intl.formatMessage({
                        defaultMessage: 'Loading error. Please try again later.',
                        id: 'error.loading'
                    })}
                </h3>
            </div>
        </div>
    }

    return isSuccess && supportBots ? (
        <BotEditorContent
            botId={botId}
            url={url}
            path={path}
            supportBots={supportBots}
            supportTopicCodes={supportTopicCodes}
            refreshData={refreshData}
            initialDataLoaded={initialDataLoaded}
        />
    ) : null;
};

const defaultBotData = {
    id: null,
    updated: "",
    updatedby: "",
    created: "",
    createdby: "",
    status: "DRAFT",
    config: {
        name: "",
        description: "",
        versionGroup: "",
        versions: [],
        version: "DEFAULT",
        coordinator: "",
        supportRequestTarget: "",
        generalInstructions: "",
        supportTopics: [],
        materials: [],
        color: "#9EA5F8",
        icon: "Bot",
        topicData: {},
    }
}

const BotEditorContent = ({ botId, url, path, supportBots, supportTopicCodes, refreshData, initialDataLoaded }) => {
    const intl = useIntl();
    const [botData, setBotData] = useState(null);
    const [originalBotData, setOriginalBotData] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(null); // null, 'DRAFT', or 'ACTIVE'
    const { setSnackbarData } = useSnackbar();
    const postCodes = PostCodes();
    const newBot = botId === 'new'
    const postBot = newBot ? PostBot() : PostBotWithId(botId);
    const { showPopup, hidePopup } = usePopup()
    let history = useHistory()

    // Add assistant-related hooks
    const {
        data: {
            assistantInstructionsConfig,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody
        } = {},
    } = useSupportBotData();

    const { instanceLang } = useLanguageData();
    const botLanguage = instanceLang || "en";

    const postAssistant = PostAssistant();
    const postAssistantWithId = PostAssistantWithId(botData?.assistantid);
    const getAssistant = GetAssistant(botData?.assistantid, !!botData?.assistantid);

    // Add vector store hooks
    const createVectorStore = CreateVectorStore();
    const addFileToVectorStore = AddFileToVectorStore();
    const removeFileFromVectorStore = RemoveFileFromVectorStore();
    const { data: vectorStoreFiles, refetch: refetchVectorStoreFiles } = GetVectorStoreFiles(botData?.vector_store_id, false);

    // Build assistant post body (similar to BotModuleTester)
    const buildAssistantPostBody = useMemo(() => {
        if (!assistantInstructionsConfig?.postBody || !botData?.config) return null;

        const processed = replaceInstructionVariables(
            assistantInstructionsConfig,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody,
            botLanguage
        );

        // Name: bot name + (id)
        processed.postBody.name = `${botData.config.name || "SupportBot"} (${botData.id || 'new'})`;

        const botSection = processSingleBotInstructions({
            bot: botData,
            processedConfig: processed,
            intl,
            categoryData: null
        });

        // Merge into main instructions
        processed.postBody.instructions = (processed.postBody.instructions || "")
            .replace(/{botInstructions}/g, botSection.trim())
            .replace(/{initialMessage}/g, "");

        // Ensure file_search tool is present if there are materials
        processed.postBody.tools = ensureFileSearchTool(
            processed.postBody.tools,
            botData.config.materials
        );

        return processed.postBody;
    }, [assistantInstructionsConfig, botData, intl, supportBotInstructions, supportTopicInstructionBody, supportBotInstructionBody, botLanguage]);

    // Initialize bot data from API
    const initializeBotData = useCallback((id) => {
        // Check for new bot template
        if (id === 'new') {
            const templateData = sessionStorage.getItem('newBotTemplate');
            if (templateData) {
                const parsedTemplate = JSON.parse(templateData);

                return {
                    id: null,
                    updated: "",
                    updatedby: "",
                    created: "",
                    createdby: "",
                    status: "DRAFT",
                    config: {
                        ...parsedTemplate.config,
                        topicData: parsedTemplate.config.topicData || {}
                    }
                };
            }
        }

        const getBot = supportBots.find(bot => Number(bot.id) === Number(id));

        if (getBot) {
            const normalizedConfig = {
                ...getBot.config,
                topicData: getBot.config.topicData || {}
            };

            // Set version to DEFAULT if missing or empty
            if (!normalizedConfig?.version || normalizedConfig?.version === "") {
                normalizedConfig.version = "DEFAULT";
            }

            // Generate versionGroup if missing or empty
            if (!normalizedConfig?.versionGroup || normalizedConfig?.versionGroup === "") {
                normalizedConfig.versionGroup = getUniqueKey();
            }

            // Initialize versions array if missing or empty
            if (!normalizedConfig?.versions || normalizedConfig?.versions.length === 0) {
                normalizedConfig.versions = ["DEFAULT"];
            }

            return {
                ...getBot,
                config: normalizedConfig
            };
        }

        return defaultBotData

    }, [supportBots]);

    // Reset component when navigating to a different bot
    useEffect(() => {
        if (initialDataLoaded && supportBots) {
            const sessionData = BotSessionData.load(botId)
            const originalSessionData = BotSessionData.loadOriginal(botId) || null

            const initialData = sessionData || initializeBotData(botId)
            setBotData(initialData)

            if (sessionStorage.getItem('newBotTemplate')) {
                setOriginalBotData(defaultBotData)
                setHasChanges(true)

                BotSessionData.save(botId, initialData, defaultBotData);
                sessionStorage.removeItem('newBotTemplate');

                return;
            }

            const initialOriginalData = originalSessionData || JSON.parse(JSON.stringify(initialData))
            setOriginalBotData(initialOriginalData)

            if (sessionData && originalSessionData) {
                const hasChanges = JSON.stringify(sessionData) !== JSON.stringify(originalSessionData);
                setHasChanges(hasChanges)
            } else {
                setHasChanges(false)
            }
        }
    }, [botId, initializeBotData, initialDataLoaded, supportBots])

    const refreshDataOnDemand = useCallback(() => {
        if (refreshData) {
            refreshData()
        }
    }, [refreshData])

    // Track changes and persist to session storage
    useEffect(() => {
        if (botData && originalBotData) {
            const currentDataString = JSON.stringify(botData);
            const originalDataString = JSON.stringify(originalBotData);
            const dataHasChanges = currentDataString !== originalDataString;

            setHasChanges(dataHasChanges)

            if (dataHasChanges) {
                BotSessionData.save(botId, botData, originalBotData)
            } else {
                BotSessionData.clear(botId)
            }
        }
    }, [botData, originalBotData, botId])

    const updateRootData = useCallback((field, value) => {
        setBotData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const updateBotData = useCallback((field, value) => {
        setBotData(prev => ({
            ...prev,
            config: {
                ...prev.config,
                [field]: value
            }
        }));
    }, []);

    const updateNestedData = useCallback((parent, field, value) => {
        setBotData(prev => ({
            ...prev,
            config: {
                ...prev.config,
                [parent]: {
                    ...prev.config[parent],
                    [field]: value
                }
            }
        }));
    }, []);

    // Function to manage vector store creation and file syncing
    const handleVectorStore = async (updatedBotData) => {
        return await syncVectorStoreFiles({
            botData,
            updatedBotData,
            createVectorStoreFn: createVectorStore.mutateAsync,
            addFileFn: addFileToVectorStore.mutateAsync,
            removeFileFn: removeFileFromVectorStore.mutateAsync,
            refetchFilesFn: refetchVectorStoreFiles
        });
    };

    // Function to ensure assistant is created/updated
    const ensureAssistantReady = async (updatedBotData, vectorStoreId) => {
        if (!buildAssistantPostBody) {
            console.warn("Assistant config missing, skipping assistant save");
            return updatedBotData.assistantid || null;
        }

        const currentAssistantId = updatedBotData.assistantid;

        try {
            const postBody = _.cloneDeep(buildAssistantPostBody);

            // Add vector store to tool_resources if available
            if (vectorStoreId) {
                postBody.tool_resources = {
                    file_search: {
                        vector_store_ids: [vectorStoreId]
                    }
                };
            } else if (getAssistant.data?.tool_resources) {
                // Preserve existing tool_resources if no new vector store
                postBody.tool_resources = getAssistant.data.tool_resources;
            }

            // Update existing assistant
            if (currentAssistantId) {
                // Wait for getAssistant to finish if it's loading
                if (getAssistant.isLoading) {
                    let attempts = 0;
                    while (attempts < 30 && getAssistant.isLoading) {
                        await new Promise((r) => setTimeout(r, 200));
                        attempts += 1;
                    }
                }

                if (getAssistant.isSuccess && getAssistant.data) {
                    // Preserve existing tools if not explicitly set in postBody
                    if (!postBody.tools && getAssistant.data.tools) {
                        postBody.tools = getAssistant.data.tools;
                    }

                    const res = await postAssistantWithId.mutateAsync(postBody);
                    return res?.data?.id || currentAssistantId;
                }
            }

            // Create new assistant
            const createRes = await postAssistant.mutateAsync(postBody);
            const newId = createRes?.data?.id;
            if (!newId) throw new Error("Assistant creation failed");
            return newId;

        } catch (error) {
            console.error("Error saving assistant:", error);
            throw error;
        }
    };

    const handleSave = useCallback(async (newStatus = null, navigationLocation) => {
        // Set saving state to track which button was clicked
        setIsSaving(newStatus || 'ACTIVE');

        // Copy the current state
        const updatedBotData = { ...botData };

        // Check if we need to assign a versionGroup
        const needsVersionGroup =
            !updatedBotData?.config?.versionGroup ||
            updatedBotData.config.versionGroup === "";

        if (needsVersionGroup) {
            const uniqueKey = getUniqueKey()
            updatedBotData.config.versionGroup = uniqueKey
        }

        if (newStatus) {
            updatedBotData.status = newStatus
        }

        // Handle vector store creation and file syncing
        let vectorStoreId = null;
        try {
            vectorStoreId = await handleVectorStore(updatedBotData);
            if (vectorStoreId) {
                updatedBotData.vector_store_id = vectorStoreId;
            }
        } catch (error) {
            setIsSaving(null);
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'vectorStoreSaveFailed',
                    defaultMessage: 'Failed to save reference materials. Please try again.'
                }),
                state: "error",
                autoHide: true,
            });
            return; // Stop save process if vector store fails
        }

        // Save assistant first and get the assistantid
        try {
            const assistantId = await ensureAssistantReady(updatedBotData, vectorStoreId);
            if (assistantId) {
                updatedBotData.assistantid = assistantId;
            }
        } catch (error) {
            setIsSaving(null);
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'assistantSaveFailed',
                    defaultMessage: 'Failed to save assistant. Please try again.'
                }),
                state: "error",
                autoHide: true,
            });
            return; // Stop save process if assistant fails
        }

        const { topicData } = updatedBotData.config;
        const botVersion = updatedBotData.config.version;
        const isDefaultVersion = !botVersion || botVersion === "DEFAULT";

        // Create a copy of bot data without the topicData for the bot API
        const botDataForApi = {
            ...updatedBotData,
            config: { ...updatedBotData.config }
        };
        delete botDataForApi.config.topicData;
        delete botDataForApi.config.versions;

        // Prepare topic data for saving with version support
        const prepareTopicDataForPost = () => {
            const result = [];

            Object.entries(topicData).forEach(([topicId, currentTopicData]) => {
                const originalTopic = supportTopicCodes[topicId] || {};

                if (isDefaultVersion) {
                    const updatedTopic = {
                        ...originalTopic,
                        ...currentTopicData,
                        versions: originalTopic.versions || {}
                    };

                    result.push({
                        "codeset": "category",
                        "code": topicId,
                        "value": updatedTopic
                    });
                } else {
                    const baseValues = { ...originalTopic };
                    delete baseValues.versions;

                    const versions = { ...(originalTopic.versions || {}) };
                    const versionChanges = {};
                    let hasChanges = false;

                    Object.keys(currentTopicData).forEach(propKey => {
                        if (currentTopicData[propKey] !== baseValues[propKey]) {
                            versionChanges[propKey] = currentTopicData[propKey];
                            hasChanges = true;
                        }
                    });

                    if (hasChanges) {
                        versions[botVersion] = versionChanges;

                        const updatedTopic = {
                            ...originalTopic,
                            versions
                        };

                        result.push({
                            "codeset": "category",
                            "code": topicId,
                            "value": updatedTopic
                        });
                    }
                }
            });

            return result;
        };

        let botSaved = false;
        let codesSaved = false;
        let savedBotId = false;

        const checkBothSaved = () => {
            if (botSaved && codesSaved) {
                setIsSaving(null);
                BotSessionData.clear(botId)
                refreshDataOnDemand()

                setOriginalBotData(JSON.parse(JSON.stringify(updatedBotData)));
                setHasChanges(false);

                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'saveSuccess',
                        defaultMessage: 'Changes saved successfully!'
                    }),
                    state: "success",
                    autoHide: true,
                });

                if (navigationLocation) {
                    history.push(navigationLocation)
                }

                if (savedBotId) {
                    const currentPath = history.location.pathname;
                    let currentTab = '/information';

                    if (currentPath.includes('/instructions')) {
                        currentTab = '/instructions';
                    } else if (currentPath.includes('/materials')) {
                        currentTab = '/materials';
                    } else if (currentPath.includes('/information')) {
                        currentTab = '/information';
                    }
                    history.push(`/support-bot/${savedBotId}${currentTab}`)
                }
            }
        };

        // Save bot data
        postBot.mutate(botDataForApi, {
            onError: error => {
                setIsSaving(null);
                console.error("Error saving bot:", error);
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'saveFailed',
                        defaultMessage: 'Failed to save changes. Please try again.'
                    }),
                    state: "error",
                    autoHide: true,
                });
            },
            onSuccess: (response) => {
                savedBotId = response?.data?.id
                botSaved = true;
                checkBothSaved();
            }
        });

        // Save topic codes with version support
        const topicDataToPost = prepareTopicDataForPost();
        if (topicDataToPost.length > 0) {
            postCodes.mutate(topicDataToPost, {
                onError: error => {
                    setIsSaving(null);
                    console.error("Error saving topic codes:", error);
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage({
                            id: 'saveFailed',
                            defaultMessage: 'Failed to save topic instructions. Please try again.'
                        }),
                        state: "error",
                        autoHide: true,
                    });
                },
                onSuccess: () => {
                    codesSaved = true;
                    checkBothSaved();
                }
            });
        } else {
            codesSaved = true;
            checkBothSaved();
        }

        // Keep data in state for continued editing
        setBotData(updatedBotData);
    }, [botData, intl, botId, postBot, postCodes, setSnackbarData, supportTopicCodes, buildAssistantPostBody, getAssistant, postAssistant, postAssistantWithId]);

    const [navigateTo, setNavigateTo] = useState(null)

    useEffect(() => {
        if (navigateTo && !hasChanges) {
            hidePopup();
            history.push(navigateTo);
            setNavigateTo(null);
        }
    }, [hasChanges, navigateTo, hidePopup, history])

    const handleDiscard = useCallback((location) => {
        BotSessionData.clear(botId)
        setHasChanges(false)
        setNavigateTo(location)
    }, [])

    const showUnsavedChangesPrompt = useCallback((location) => {
        showPopup(
            <UnsavedChangesPrompt
                handleSave={() => {
                    hidePopup()
                    handleSave(null, location)
                }}
                onCancel={() => {
                    hidePopup()
                }}
                onDiscard={() => {
                    handleDiscard(location)
                }}
            />,
            'confirm'
        )
    }, [showPopup, hidePopup, handleSave, handleDiscard])

    if (!botData) {
        return <BotEditorSkeleton />
    }

    const isDraft = botData?.status?.toLowerCase() === "draft";

    const placeholderName = intl.formatMessage({id: "createNewSupportBot", defaultMessage: "New Bot Module"})

    return (
        <div className={"survey-container bot-editor"}>
            <Prompt
                when={hasChanges}
                message={(location) => {
                    if (location.pathname.includes(`/support-bot/${botId}/`)) {
                        return true;
                    }

                    showUnsavedChangesPrompt(location);
                    return false;
                }}
            />
            <div className={"survey-header header"}>
                <Link to="/support-bots" className={"back"}>
                    <BackArrow />
                </Link>
                <h3>{botData.config.name || placeholderName}</h3>
                <BotStatus
                    status={botData?.status || "DRAFT"}
                    updateBotData={updateRootData}
                />
                {hasChanges &&
                    <p className={"unsaved-changes"}>
                        {intl.formatMessage({
                            id: 'survey.header.unsavedChanges',
                            defaultMessage: 'Unsaved changes',
                        })}
                    </p>
                }
                <SaveEdits
                    handleSave={handleSave}
                    hasChanges={hasChanges}
                    botData={supportBots}
                    botId={botId}
                    currentBot={botData}
                    isDraft={isDraft}
                    isSaving={isSaving}
                />
            </div>
            <div className={"survey-view"}>
                <div className={"survey-content"}>
                    <BotNavigation url={url} />
                    <BotContent
                        url={url}
                        path={path}
                        botData={botData.config}
                        updateBotData={updateBotData}
                        updateNestedData={updateNestedData}
                    />
                </div>
                <BotModuleTester botData={botData} updateRootData={updateRootData}/>
            </div>
        </div>
    );
};

const BotStatus = ({status, updateBotData}) => {
    const intl = useIntl()
    const statusIsActive = status === "ACTIVE"
    return <div className={`bot-status ${status}`}>
        <StatusIcon />
        {intl.formatMessage({
            defaultMessage: status,
            id: `bot.status.${status}`,
        })}
        {statusIsActive &&
            <div className={"change-status"}>
                <button className={"status-button"}>
                    {intl.formatMessage({
                        defaultMessage:"Edit",
                        id:"edit"
                    })}
                </button>
                <select
                    className={"status-select"}
                    value={status}
                    onChange={e => updateBotData("status", e.target.value)}>
                    <option value={"DRAFT"}>
                        {intl.formatMessage({
                            defaultMessage: "Draft",
                            id: `bot.status.DRAFT`,
                        })}
                    </option>
                    <option value={"ACTIVE"}>
                        {intl.formatMessage({
                            defaultMessage: "Active",
                            id: `bot.status.ACTIVE`,
                        })}
                    </option>
                </select>
            </div>
        }
    </div>
}

const BotNavigation = ({ url }) => {
    const intl = useIntl();

    return (
        <div className={"survey-navigation"}>
            <nav>
                <ul>
                    <li>
                        <NavLink to={`${url}/information`}>
                            <Settings size={16} />
                            {intl.formatMessage({
                                id: 'survey.navigation.information',
                                defaultMessage: 'Information',
                            })}
                        </NavLink>
                    </li>
                    <li>
                        <NavLink to={`${url}/instructions`}>
                            <BookOpen size={16} />
                            {intl.formatMessage({
                                id: 'instructions',
                                defaultMessage: 'Instructions',
                            })}
                        </NavLink>
                    </li>
                    <li>
                        <NavLink to={`${url}/materials`}>
                            <Files size={16} />
                            {intl.formatMessage({
                                id: 'referenceMaterials',
                                defaultMessage: 'Materials',
                            })}
                        </NavLink>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

const BotContent = ({ url, path, botData, updateBotData, updateNestedData }) => {
    return (
        <Switch>
            <Route path={`${path}/information`}>
                <div className={`survey-edit bot-content`}>
                    <BotInformation
                        botData={botData}
                        updateBotData={updateBotData}
                    />
                </div>
            </Route>
            <Route path={`${path}/instructions`}>
                <div className={`survey-edit bot-content`}>
                    <BotInstructions
                        botData={botData}
                        updateBotData={updateBotData}
                        updateNestedData={updateNestedData}
                    />
                </div>
            </Route>
            <Route path={`${path}/materials`}>
                <div className={`survey-edit bot-content`}>
                    <BotMaterials
                        botData={botData}
                        updateBotData={updateBotData}
                    />
                </div>
            </Route>
            <Route path={path}>
                <Redirect to={`${url}/information`} />
            </Route>
        </Switch>
    );
};

const SaveEdits = ({ handleSave, hasChanges, botData, botId, currentBot, isDraft, isSaving }) => {
    const intl = useIntl();
    const isAnySaving = isSaving !== null;

    return (
        <div className={"save-toolbar"}>
            <BotVersionSelector botData={botData} botId={botId} currentBot={currentBot}/>
            {isDraft ?
            <>
                <button
                    className={`draft ${!hasChanges || isAnySaving ? 'disabled' : ''}`}
                    onClick={()=>handleSave('DRAFT')}
                    disabled={!hasChanges || isAnySaving}
                >
                    {isSaving === 'DRAFT' ? (
                        <>
                            <Loader2 size={16} className="spinner" />
                            {intl.formatMessage({
                                defaultMessage: 'Saving...',
                                id: 'saving'
                            })}
                        </>
                    ) : (
                        intl.formatMessage({
                            defaultMessage: 'Save draft',
                            id: 'saveDraft'
                        })
                    )}
                </button>
                <button
                    className={`save ${isAnySaving ? 'disabled' : ''}`}
                    onClick={()=>handleSave('ACTIVE')}
                    disabled={isAnySaving}
                >
                    {isSaving === 'ACTIVE' ? (
                        <>
                            <Loader2 size={16} className="spinner" />
                            {intl.formatMessage({
                                defaultMessage: 'Publishing...',
                                id: 'publishing'
                            })}
                        </>
                    ) : (
                        intl.formatMessage({
                            defaultMessage: 'Publish',
                            id: 'publish'
                        })
                    )}
                </button>
            </> :
                <button
                    className={`save ${!hasChanges || isAnySaving ? 'disabled' : ''}`}
                    onClick={()=>handleSave('ACTIVE')}
                    disabled={!hasChanges || isAnySaving}
                >
                    {isSaving === 'ACTIVE' ? (
                        <>
                            <Loader2 size={16} className="spinner" />
                            {intl.formatMessage({
                                defaultMessage: 'Saving...',
                                id: 'saving'
                            })}
                        </>
                    ) : (
                        intl.formatMessage({
                            defaultMessage: 'Save changes',
                            id: 'saveChanges'
                        })
                    )}
                </button>
            }
        </div>
    );
};

function BotEditorSkeleton() {
    return <div className={"survey-container skeleton-container"}>
        <div className={"survey-header"}>
            <Skeleton width={17} height={17} circle={true} noMargin={true} classText={"back"}/>
            <Skeleton width={250} height={24} noMargin={true}/>
            <Skeleton width={57} height={17} noMargin={true} classText={"status"}/>
            <div className={"save-toolbar"}>
                <Skeleton width={150} height={40} noMargin={true} classText={"draft"}/>
                <Skeleton width={90} height={40} noMargin={true} classText={"save"}/>
            </div>
        </div>
        <div className={"survey-view"}>
            <div className={"survey-content"}>
                <div className={"survey-navigation"}>
                    <Skeleton width={80} height={20} noMargin={true}/>
                    <Skeleton width={80} height={20} noMargin={true}/>
                    <Skeleton width={80} height={20} noMargin={true}/>
                </div>
                <div className={"survey-edit"}>
                    <Skeleton width={40} height={20}/>
                    <Skeleton height={55} noMargin={true} classText={"input"}/>
                    <div className={"survey-time"}>
                        <div>
                            <Skeleton width={40} height={20}/>
                            <Skeleton height={55} noMargin={true} classText={"input"}/>
                        </div>
                        <div>
                            <Skeleton width={40} height={20}/>
                            <Skeleton height={55} noMargin={true} classText={"input"}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}

const UnsavedChangesPrompt = ({ handleSave, onCancel, onDiscard }) => {
    const intl = useIntl();

    return (
        <Modal
            header={intl.formatMessage({
                id: 'survey.unsavedChanges.header',
                defaultMessage: 'Save changes?',
            })}
            text={
                <FormattedMessage
                    id="survey.unsavedChanges.text"
                    defaultMessage="Do you wish to save changes before leaving page?{br}Any unsaved changes will be lost."
                    values={{ br: <br /> }}
                />
            }
            discardText={intl.formatMessage({
                id: 'survey.unsavedChanges.discard',
                defaultMessage: "Don't save",
            })}
            confirmText={intl.formatMessage({
                id: 'survey.save.save',
                defaultMessage: "Save changes",
            })}
            discardAction={onDiscard}
            closeModal={onCancel}
            confirmAction={handleSave}
        />
    );
};